import { useCallback, useMemo } from "react";

import type { ColDef } from "ag-grid-community";
import { deleteMany as deleteSupportingDocuments } from "@synthesis/shared-services/files/supporting-document/client";
import { downloadSupportingDocumentFile } from "@synthesis/shared-services";
import { Button, setPromiseSnack, setSnack, Table } from "@synthesis/shared-ui";
import { cn, useDownload } from "@synthesis/shared-utils";
import { IconExportUpload } from "@synthesis/shared-icons";

import { useUserInfo } from "../../../../shared/hooks/use-user-info";
import { usePageStore } from "../../application-detail-page.lib";
import { useReviewDocuments, type ReviewDocument } from "../../hooks/use-review-documents";
import { DetailTabPlaceholder } from "../detail-tab-placeholder";
import { ReviewDocumentActionsCell } from "./review-document-actions-cell";
import { ReviewDocumentFileNameCell } from "./review-document-file-name-cell";
import { formatUploadDate, type ReviewDocumentRow } from "./review-documents-tab.lib";
import { TuPartsContent } from "./tu-parts-content";

type ReviewDocumentsContentProps = {
  requestId: string;
  canUpload: boolean;
  canDelete: boolean;
  canUploadTuParts: boolean;
  canDeleteTuParts: boolean;
  className?: string;
};

const SNACK_TITLES_DELETE = {
  pending: "Выполняется удаление документа...",
  fulfilled: "Документ удален",
  rejected: "Не удалось удалить документ. Попробуйте еще раз",
} as const;

const REVIEW_DOCUMENT_TABLE_COLUMNS: ColDef<ReviewDocumentRow>[] = [
  {
    headerName: "Документ",
    colId: "fileName",
    cellRenderer: ReviewDocumentFileNameCell,
    cellClass: "flex items-center",
    flex: 1,
    minWidth: 260,
  },
  {
    headerName: "Автор",
    colId: "authorName",
    field: "authorName",
    width: 300,
  },
  {
    headerName: "Дата загрузки",
    colId: "createdAt",
    field: "createdAt",
    valueFormatter: ({ value }) => formatUploadDate(String(value ?? "")),
    width: 170,
  },
  {
    headerName: "",
    colId: "actions",
    cellRenderer: ReviewDocumentActionsCell,
    cellClass: "flex items-center justify-center",
    width: 90,
    sortable: false,
    filter: false,
    suppressHeaderMenuButton: true,
  },
];

export const ReviewDocumentsContent = ({
  requestId,
  canUpload,
  canDelete,
  canUploadTuParts,
  canDeleteTuParts,
  className,
}: ReviewDocumentsContentProps) => {
  const userInfo = useUserInfo();
  const downloadFile = useDownload();
  const pageStore = usePageStore();

  const { documents, authorsMap, isLoading, isError, invalidateReviewDocuments } = useReviewDocuments(requestId);

  const handleDownload = useCallback(
    async (document: ReviewDocument) => {
      const promise = async () => {
        const blob = await downloadSupportingDocumentFile(document.id);
        downloadFile({ id: document.id, baseName: document.baseName, ...(document.ext ? { ext: document.ext } : {}) }, blob);
      };

      try {
        await promise();
      } catch {
        setSnack({
          messageType: "error",
          title: "Не удалось скачать файл",
        });
      }
    },
    [downloadFile],
  );

  const handleDelete = useCallback(
    async (document: ReviewDocument) => {
      if (!userInfo?.id) {
        return;
      }

      const promise = (async () => {
        await deleteSupportingDocuments({ body: { initUserId: userInfo.id, ids: [document.id] } });
        await invalidateReviewDocuments();
      })();

      await setPromiseSnack({ promise, promiseSettings: { title: SNACK_TITLES_DELETE } });
    },
    [invalidateReviewDocuments, userInfo?.id],
  );

  const rows = useMemo<ReviewDocumentRow[]>(
    () =>
      documents.map((document) => ({
        ...document,
        authorName: authorsMap.get(document.authorId)?.name ?? "—",
      })),
    [documents, authorsMap],
  );

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="subtitle-lg text-type-common">Сопроводительные документы</h3>
          <p className="subtitle-sm text-type-minor">Телеграммы, акты, письма и иные документы, сопровождающие рассмотрение заявки</p>
        </div>
        {canUpload ? (
          <Button
            type="button"
            theme="text"
            size="sm"
            onClick={() => pageStore?.setPopupAction({ type: "upload-review-document", requestId })}
            leftIcon={<IconExportUpload className="size-5" />}
          >
            <span className="text-type-minor">Загрузить</span>
          </Button>
        ) : null}
      </div>

      {isError ? (
        <p className="paragraph-sm text-type-error py-4 text-center">Не удалось загрузить документы</p>
      ) : documents.length === 0 ? (
        <DetailTabPlaceholder title="Документы отсутствуют" description="После загрузки документы будут доступны в таблице" />
      ) : (
        <Table
          className="w-full"
          tableId="review-documents-table"
          tableColumns={REVIEW_DOCUMENT_TABLE_COLUMNS}
          tableData={rows}
          getRowId={({ data }: { data?: ReviewDocumentRow }) => data?.id ?? ""}
          context={{ canDelete, onDownload: handleDownload, onDelete: handleDelete }}
          isLoading={isLoading}
          isSortable={false}
          hasFilters={false}
          suppressHeaderContextMenu
          domLayout="autoHeight"
        />
      )}

      <TuPartsContent requestId={requestId} canUpload={canUploadTuParts} canDelete={canDeleteTuParts} />
    </div>
  );
};
